import { useState, useEffect, useRef } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { addMetricItems, 
  processMetric, 
  createMetric, 
  updateMetric, 
  getMetric, 
  getMetricItems,
  getMetricProcessingStatus
} from '../../services/metricsService';
import MetricsProgressPage from './MetricsProgressPage';
import MetricsResultsPage from './MetricsResultsPage';
import MetricsSegmentsPanel from './MetricsSegmentsPanel';
import MetricsOptionsPanel from './MetricsOptionsPanel';
import LoadingSpinner from '../common/LoadingSpinner';
import clearIcon from '../../assets/icons/clear.svg';
import sampleIcon from '../../assets/icons/sample.svg';
import { generateID } from '../../utils/idGenerator';

const MetricsPage = () => {
  const { headerRef } = useOutletContext();
  const { id } = useParams();
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluationError, setEvaluationError] = useState('');
  const [currentView, setCurrentView] = useState('metrics'); // 'metrics', 'progress', 'results'
  const [hasError, setHasError] = useState(false);
  const [metricResults, setMetricResults] = useState(null);
  const [hasSampleText, setHasSampleText] = useState(false);
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  const [selectedMetricIds, setSelectedMetricIds] = useState([]);
  const [isLoadingMetric, setIsLoadingMetric] = useState(false);
  const [metricItemsData, setMetricItemsData] = useState(null);
  const [metricExists, setMetricExists] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [localMetricId, setLocalMetricId] = useState('');
  const [isLocalDraft, setIsLocalDraft] = useState(false);
  const pendingMetricIdRef = useRef(null);

  const effectiveMetricId = id || localMetricId;
  useEffect(() => {
    if (id && id !== localMetricId) {
      setLocalMetricId(id);
      if (pendingMetricIdRef.current === id) {
        setIsLocalDraft(true);
        pendingMetricIdRef.current = null;
      } else {
        setIsLocalDraft(false);
      }
    }
  }, [id, localMetricId]);

  useEffect(() => {
    if (effectiveMetricId && headerRef?.current?.setMetricId) {
      headerRef.current.setMetricId(effectiveMetricId);
    }
  }, [effectiveMetricId, headerRef]);

  const handleEnsureMetricId = () => {
    if (effectiveMetricId) return effectiveMetricId;
    const newId = generateID();
    setLocalMetricId(newId);
    setIsLocalDraft(true);
    pendingMetricIdRef.current = newId;
    if (headerRef?.current?.setMetricId) {
      headerRef.current.setMetricId(newId);
    }
    window.history.replaceState(null, "", `/metrics/${newId}`);
    return newId;
  };

  // Initialize segments with 3 columns (source, target, reference)
  const [segments, setSegments] = useState([
    { id: 1, source: '', target: '', reference: '' },
    { id: 2, source: '', target: '', reference: '' },
    { id: 3, source: '', target: '', reference: '' },
  ]);

  useEffect(() => {
    if (headerRef?.current?.setEvaluationName) {
      headerRef.current.setEvaluationName('Untitled Metrics Run');
    }
  }, [headerRef]);

  useEffect(() => {
    const loadExistingMetric = async () => {
      if (effectiveMetricId) {
        if (isLocalDraft && effectiveMetricId === localMetricId) {
          setIsLoadingMetric(false);
          return;
        }
        if (currentView !== 'progress') {
          setIsLoadingMetric(true);
        }
        setMetricExists(false); // Reset state when id changes
        try {
          const metric = await getMetric(effectiveMetricId);
          console.log('Metric data:', metric);
          setMetricExists(true); // Metric exists
         
          const metricItems = await getMetricItems(effectiveMetricId);
          setMetricItemsData(metricItems);

          const metricData = metric.result || metric;
          setMetricResults(metricData);

          // Check if metric is completed
          if (metric && metric.metric && metric.metric.completed) {
            setCurrentView('results');
            
            if (headerRef?.current?.setEvaluationName && metric.metric.name) {
              headerRef.current.setEvaluationName(metric.metric.name);
            }
          } 
          // Check if metric has started property (in metricData or metric.metric)
          else if ((metricData && metricData.started) || (metric && metric.metric && metric.metric.started)) {
            setCurrentView('progress');
          } 
          else {
            if (currentView !== 'progress') {
              setCurrentView('metrics');
            }
          }
        } catch (error) {
          console.log('Metric not found, creating new metric:', effectiveMetricId);
          setMetricExists(false); // Metric doesn't exist
          if (currentView !== 'progress') {
            setCurrentView('metrics');
          }
        } finally {
          setIsLoadingMetric(false);
        }
      }
    };

    loadExistingMetric();
    // eslint-disable-next-line
  }, [effectiveMetricId, headerRef, isLocalDraft, localMetricId]);


  const handleAddSegment = () => {
    const newId = segments.length > 0 ? Math.max(...segments.map(s => s.id)) + 1 : 1;
    setSegments([...segments, { id: newId, source: '', target: '', reference: '' }]);
  };

  const handleRemoveSegment = (segmentId) => {
    if (segments.length > 1) {
      setSegments(segments.filter(segment => segment.id !== segmentId));
    }
  };

  const handleSegmentChange = (segmentId, field, value) => {
    setSegments(prev => prev.map(segment =>
      segment.id === segmentId ? { ...segment, [field]: value } : segment
    ));
  };


  const handleLoadSampleText = () => {
    setSegments([
      { 
        id: 1, 
        source: 'The quick brown fox jumps over the lazy dog.', 
        target: 'El zorro marrón rápido salta arriba el perro perezoso.', 
        reference: 'El rápido zorro marrón salta sobre el perro perezoso.' 
      },
      { 
        id: 2, 
        source: 'She sells seashells by the seashore.', 
        target: 'Ella vende conchas en la costa del mar.', 
        reference: 'Ella vende conchas marinas en la orilla del mar.' 
      },
      { 
        id: 3, 
        source: 'Knowledge is power.', 
        target: 'Conocimiento es poder.', 
        reference: 'El conocimiento es poder.' 
      },
      { 
        id: 4, 
        source: 'You dont have any events.', 
        target: 'No tienes ningún evento.', 
        reference: 'No tienes ningún evento.' 
      },
      {
        id: 5,
        source: 'You have access to some key features that drive member engagement! Here’s what you should set up in the first week to get it going.', 
        target: 'Tienes acceso a algunas características clave que impulsan la participación de los miembros. Aquí tienes lo que debes configurar en la primera semana para que todo comience.', 
        reference: '¡Tienes acceso a algunas funciones clave que impulsan la participación de los miembros! Esto es lo que deberías configurar en la primera semana para ponerlo en marcha.' 
      }
    ]);
    setHasSampleText(true);
  };

  const handleClearText = () => {
    setSegments([
      { id: 1, source: '', target: '', reference: '' },
      { id: 2, source: '', target: '', reference: '' },
      { id: 3, source: '', target: '', reference: '' },
    ]);
    setHasSampleText(false);
  };

  const calculateTotalCharacters = () => {
    return segments.reduce((total, segment) => {
      return total + segment.source.length + segment.target.length + segment.reference.length;
    }, 0);
  };

  const handleFileUpload = (newSegments) => {
    if (newSegments && newSegments.length > 0) {
      const formattedSegments = newSegments.map((segment, index) => ({
        id: index + 1,
        source: segment.source || '',
        target: segment.target || '',
        reference: segment.reference || ''
      }));
      setSegments(formattedSegments);
      setHasSampleText(false);
      setHasUploadedFile(true);
    } else {
      setSegments([
        { id: 1, source: '', target: '', reference: '' },
        { id: 2, source: '', target: '', reference: '' },
        { id: 3, source: '', target: '', reference: '' },
      ]);
      setHasSampleText(false);
      setHasUploadedFile(false);
    }
  };

  // Functions to handle metric creation and updates
  const handleCreateMetric = async (selectedMetrics, email, overrideMetricId) => {
    const metricIdToUse = overrideMetricId || effectiveMetricId;
    if (!metricIdToUse || !selectedMetrics || selectedMetrics.length === 0) {
      throw new Error('Missing metric ID or selected metrics');
    }

    const name = getEvaluationName();
    await createMetric(metricIdToUse, name, email);
    setIsLocalDraft(false);
    setMetricExists(true);
    console.log('Created metric:', metricIdToUse);
  };

  const handleUpdateMetric = async (field, value, overrideMetricId) => {
    const metricIdToUse = overrideMetricId || effectiveMetricId;
    if (!metricIdToUse) {
      throw new Error('Missing metric ID');
    }

    await updateMetric(metricIdToUse, field, value);
    // console.log(`Updated metric ${field}:`, value);
  };

  const getEvaluationName = () => {
    return headerRef?.current?.getEvaluationName?.() || 'Untitled Metrics Evaluation';
  };

  const handleAddMetricItems = async (selectedMetrics, overrideMetricId) => {
    const metricIdToUse = overrideMetricId || effectiveMetricId;
    if (!metricIdToUse || !selectedMetrics || selectedMetrics.length === 0) {
      throw new Error('Missing metric ID or selected metrics');
    }

    const formSegments = segments.map(segment => ({
      sourceText: segment.source || '',
      targetText: segment.target || '',
      referenceText: segment.reference || ''
    }));

    await addMetricItems(metricIdToUse, formSegments);
    console.log('addMetricItems', formSegments);
  };

  const handleProcessMetric = async (overrideMetricId) => {
    const metricIdToUse = overrideMetricId || effectiveMetricId;
    await processMetric(metricIdToUse);
    console.log('Processed metrics:', metricIdToUse);
  };

  const handleStartEvaluation = async (metricsFromPanel) => {
    const hasContent = segments.some(segment => 
      segment.source.trim() || segment.target.trim() || segment.reference.trim()
    );

    if (!hasContent) {
      setEvaluationError('Please enter at least one segment with source, target, or reference text');
      return;
    }

    if (metricsFromPanel && metricsFromPanel.length > 0) {
      setSelectedMetricIds(metricsFromPanel);
    }

    // Use stored selectedMetricIds if not provided (e.g., for retry)
    const metricsToUse = metricsFromPanel && metricsFromPanel.length > 0 
      ? metricsFromPanel 
      : selectedMetricIds;

    if (!metricsToUse || metricsToUse.length === 0) {
      setEvaluationError('Please select at least one metric');
      setCurrentView('metrics');
      setIsEvaluating(false);
      return;
    }

    setIsEvaluating(true);
    setEvaluationError('');
    setHasError(false);
    setProcessingStatus({ status: 'starting', progress: 0 });
    setCurrentView('progress');
  };

  useEffect(() => {
    if (!effectiveMetricId || currentView !== 'progress') {
      return;
    }

    let isMounted = true;
    let isStopped = false;
    let intervalId;
    let redirectTimeoutId;

    const pollStatus = async () => {
      try {
        const response = await getMetricProcessingStatus([effectiveMetricId]);
        const statusEntry = response?.statuses?.find((entry) => entry.id === effectiveMetricId) || response?.statuses?.[0];
        if (isMounted && statusEntry) {
          setProcessingStatus(statusEntry);

          if (statusEntry.status === 'COMPLETED' || statusEntry.status === 'succeeded') {
            isStopped = true;
            if (intervalId) {
              clearInterval(intervalId);
            }
            redirectTimeoutId = setTimeout(async () => {
              try {
                const metric = await getMetric(effectiveMetricId);
                const metricItems = await getMetricItems(effectiveMetricId);
                if (isMounted) {
                  setMetricItemsData(metricItems);
                  setMetricResults(metric?.result || metric);
                  setCurrentView('results');
                }
              } catch (error) {
                console.error('Failed to load metric results:', error);
                if (isMounted) {
                  setHasError(true);
                }
              }
            }, 5000);
          }

          if (statusEntry.status === 'ERROR') {
            isStopped = true;
            if (intervalId) {
              clearInterval(intervalId);
            }
            setHasError(true);
          }
        }
      } catch (error) {
        console.error('Failed to get processing status:', error);
        if (isMounted) {
          setHasError(true);
        }
      }
    };

    pollStatus();
    intervalId = setInterval(() => {
      if (!isStopped) {
        pollStatus();
      }
    }, 5000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
      if (redirectTimeoutId) {
        clearTimeout(redirectTimeoutId);
      }
    };
  }, [currentView, effectiveMetricId]);

  const renderMetricsView = () => (
    <>
      <div className="mb-[52px]">
        <h1 className="text-3xl font-bold text-[#020817]">
          Translation Quality Metrics
        </h1>
      </div>
      
      <div className="flex flex-col md:flex-row gap-4 md:gap-6 w-full max-w-full relative">
        <div className="flex-1 w-full max-w-full relative lg:mt-0 mt-6">
         {!hasUploadedFile && (
           <button
             onClick={hasSampleText ? handleClearText : handleLoadSampleText}
             className="absolute -top-12 right-0 h-8 bg-white hover:bg-[#F1F5F9] 
             text-[#020817] text-base font-medium rounded-md px-3 py-1 
             border border-[#E2E8F0] shadow-[0px_1px_2px_0px_#0000000D] 
             focus:outline-none focus:ring-0 flex items-center z-10"
           >
             <img src={hasSampleText ? clearIcon : sampleIcon} alt="Sample text" className="w-4 h-4 mr-2" />
             {hasSampleText ? 'Clear sample text' : 'Load sample text'}
           </button>
         )}
          <MetricsSegmentsPanel
            segments={segments}
            onSegmentChange={handleSegmentChange}
            onAddSegment={handleAddSegment}
            onRemoveSegment={handleRemoveSegment}
            onFileUpload={handleFileUpload}
          />
        </div>
        <div className="w-full md:w-80 mt-0 max-w-full">
          <div className="space-y-6 sticky top-[80px] z-30">
            <MetricsOptionsPanel
              onEvaluate={handleStartEvaluation}
              isEvaluating={isEvaluating}
              evaluationError={evaluationError}
              totalCharacters={calculateTotalCharacters()}
              totalSegments={segments.length}
              segments={segments}
              metricId={effectiveMetricId}
              onEnsureMetricId={handleEnsureMetricId}
              onCreateMetric={handleCreateMetric}
              onUpdateMetric={handleUpdateMetric}
              onAddMetricItems={handleAddMetricItems}
              onProcessMetric={handleProcessMetric}
              getEvaluationName={getEvaluationName}
              metricExists={metricExists}
            />
          </div>
        </div>
      </div>
    </>
  );

  if (isLoadingMetric) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Check if metricResults has started property
  const isStarted = metricResults && (
    metricResults.started || 
    (metricResults.metric && metricResults.metric.started)
  );

  if (!effectiveMetricId) {
    return renderMetricsView();
  }

  if (currentView === 'results') {
    return (
      <MetricsResultsPage
        metricResults={metricResults}
        metricId={effectiveMetricId}
        metricItems={metricItemsData}
      />
    );
  }

  if (currentView === 'progress' || (isStarted && currentView !== 'results')) {
    return (
      <MetricsProgressPage
        hasError={hasError}
        handleEvaluate={handleStartEvaluation}
        processingStatus={processingStatus}
        selectedMetricIds={selectedMetricIds}
        setError={setHasError}
      />
    );
  }

  return renderMetricsView();
};

export default MetricsPage;